// Step 2: adapter-specific config + auth. Only openapi + git ship in v1.
// Renders a sub-form per type; the parent owns the {config, auth_value}
// state and feeds it back into this component.

import { useState } from "react";
import type { SourceType } from "./types";
import { verifySource } from "./api";

export interface ConfigDraft {
  config: Record<string, unknown>;
  auth_value: string;
  authMethod?: "github_app" | "pat";
}

interface Props {
  type: SourceType;
  draft: ConfigDraft;
  onChange(d: ConfigDraft): void;
}

export function ConfigStep({ type, draft, onChange }: Props) {
  if (type === "openapi") return <OpenAPIForm draft={draft} onChange={onChange} />;
  if (type === "git") return <GitForm draft={draft} onChange={onChange} />;
  if (type === "redocly") return <RedoclyForm draft={draft} onChange={onChange} />;
  return <div role="alert">No form available for {type}.</div>;
}

const URL_RE = /^https?:\/\/[^\s]+$/i;

function OpenAPIForm({ draft, onChange }: { draft: ConfigDraft; onChange(d: ConfigDraft): void }) {
  const url = String(draft.config.url ?? "");
  const valid = URL_RE.test(url);
  const [verifyState, setVerifyState] = useState<
    { state: "idle" } | { state: "ok"; message: string } | { state: "error"; message: string } | { state: "loading" }
  >({ state: "idle" });

  const onVerify = async () => {
    setVerifyState({ state: "loading" });
    try {
      const r = await verifySource("openapi", { url }, draft.auth_value);
      setVerifyState({
        state: r.ok ? "ok" : "error",
        message: r.message ?? (r.ok ? "Looks good." : "Verification failed."),
      });
    } catch (err) {
      setVerifyState({ state: "error", message: (err as Error).message });
    }
  };

  return (
    <div className="maisight-sources-form" aria-label="OpenAPI config">
      <label className="maisight-sources-field">
        <span>OpenAPI spec URL</span>
        <input
          type="url"
          value={url}
          required
          placeholder="https://api.example.com/openapi.json"
          aria-invalid={url.length > 0 && !valid}
          onChange={(e) =>
            onChange({ ...draft, config: { ...draft.config, url: e.target.value } })
          }
        />
        {url.length > 0 && !valid ? (
          <span className="maisight-sources-field-error" role="alert">
            Enter a full http(s) URL.
          </span>
        ) : null}
      </label>
      <label className="maisight-sources-field">
        <span>Bearer token (optional)</span>
        <input
          type="password"
          value={draft.auth_value}
          placeholder="for private specs"
          onChange={(e) => onChange({ ...draft, auth_value: e.target.value })}
        />
      </label>
      <div className="maisight-sources-form-actions">
        <button
          type="button"
          className="maisight-sources-btn maisight-sources-btn--ghost"
          disabled={!valid || verifyState.state === "loading"}
          onClick={onVerify}
        >
          {verifyState.state === "loading" ? "Verifying…" : "Verify"}
        </button>
        {verifyState.state === "ok" ? (
          <span className="maisight-sources-verify-ok">{verifyState.message}</span>
        ) : null}
        {verifyState.state === "error" ? (
          <span className="maisight-sources-verify-err" role="alert">
            {verifyState.message}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function GitForm({ draft, onChange }: { draft: ConfigDraft; onChange(d: ConfigDraft): void }) {
  const repo = String(draft.config.repo ?? "");
  const branch = String(draft.config.branch ?? "main");
  const method = draft.authMethod ?? "github_app";
  const authProvided = method === "github_app"
    ? Boolean(draft.config.app_installation_id)
    : draft.auth_value.length > 0;

  return (
    <div className="maisight-sources-form" aria-label="Git config">
      <label className="maisight-sources-field">
        <span>Repository</span>
        <input
          type="text"
          value={repo}
          required
          placeholder="acme/docs"
          onChange={(e) =>
            onChange({ ...draft, config: { ...draft.config, repo: e.target.value, host: "github" } })
          }
        />
      </label>
      <label className="maisight-sources-field">
        <span>Branch</span>
        <input
          type="text"
          value={branch}
          onChange={(e) =>
            onChange({ ...draft, config: { ...draft.config, branch: e.target.value } })
          }
        />
      </label>
      <fieldset className="maisight-sources-field" aria-label="Auth method">
        <legend>Auth method</legend>
        <label>
          <input
            type="radio"
            name="git-auth"
            value="github_app"
            checked={method === "github_app"}
            onChange={() => onChange({ ...draft, authMethod: "github_app", auth_value: "" })}
          />
          GitHub App installation
        </label>
        <label>
          <input
            type="radio"
            name="git-auth"
            value="pat"
            checked={method === "pat"}
            onChange={() => {
              const { app_installation_id: _drop, ...rest } = draft.config;
              onChange({ ...draft, authMethod: "pat", config: rest });
            }}
          />
          Fine-grained PAT
        </label>
      </fieldset>
      {method === "github_app" ? (
        <label className="maisight-sources-field">
          <span>Installation ID</span>
          <input
            type="text"
            value={String(draft.config.app_installation_id ?? "")}
            required
            placeholder="12345"
            onChange={(e) =>
              onChange({
                ...draft,
                config: { ...draft.config, app_installation_id: e.target.value },
              })
            }
          />
        </label>
      ) : (
        <label className="maisight-sources-field">
          <span>Personal access token</span>
          <input
            type="password"
            value={draft.auth_value}
            required
            placeholder="github_pat_…"
            onChange={(e) => onChange({ ...draft, auth_value: e.target.value })}
          />
        </label>
      )}
      {!authProvided ? (
        <span className="maisight-sources-field-error" role="alert" data-testid="git-auth-missing">
          Provide an installation ID or a PAT.
        </span>
      ) : null}
    </div>
  );
}

const GITHUB_URL_RE = /^https?:\/\/github\.com\/[^/]+\/[^/]+/i;

function RedoclyForm({ draft, onChange }: { draft: ConfigDraft; onChange(d: ConfigDraft): void }) {
  // Two mutually-exclusive modes per PRD 13 §"Source adapter / Config":
  //   yaml: redocly.yaml served at a public URL
  //   git:  redocly.yaml lives in a github.com repo
  const mode: "yaml" | "git" =
    "yaml_url" in draft.config ? "yaml"
    : "git_url" in draft.config ? "git"
    : (draft.config.yaml_url || draft.config.git_url ? "git" : "yaml");
  const yamlUrl = String(draft.config.yaml_url ?? "");
  const gitUrl = String(draft.config.git_url ?? "");
  const branch = String(draft.config.branch ?? "main");
  const path = String(draft.config.path ?? "redocly.yaml");
  const yamlValid = URL_RE.test(yamlUrl);
  const gitValid = GITHUB_URL_RE.test(gitUrl);

  const switchMode = (next: "yaml" | "git") => {
    // Clear the other mode's fields so the backend's mutually-exclusive
    // check (IngestRejected("pick yaml_url OR git_url")) doesn't fire.
    if (next === "yaml") {
      onChange({
        ...draft,
        config: { yaml_url: yamlUrl },
      });
    } else {
      onChange({
        ...draft,
        config: { git_url: gitUrl, branch, path },
      });
    }
  };

  return (
    <div className="maisight-sources-form" aria-label="Redocly config">
      <fieldset className="maisight-sources-field" aria-label="Where is the redocly.yaml?">
        <legend>Where is the redocly.yaml?</legend>
        <label>
          <input
            type="radio"
            name="redocly-mode"
            value="yaml"
            checked={mode === "yaml"}
            onChange={() => switchMode("yaml")}
          />
          Public URL
        </label>
        <label>
          <input
            type="radio"
            name="redocly-mode"
            value="git"
            checked={mode === "git"}
            onChange={() => switchMode("git")}
          />
          GitHub repository
        </label>
      </fieldset>
      {mode === "yaml" ? (
        <label className="maisight-sources-field">
          <span>redocly.yaml URL</span>
          <input
            type="url"
            value={yamlUrl}
            required
            placeholder="https://api.acme.com/redocly.yaml"
            aria-invalid={yamlUrl.length > 0 && !yamlValid}
            onChange={(e) =>
              onChange({ ...draft, config: { yaml_url: e.target.value } })
            }
          />
          {yamlUrl.length > 0 && !yamlValid ? (
            <span className="maisight-sources-field-error" role="alert">
              Enter a full http(s) URL.
            </span>
          ) : null}
        </label>
      ) : (
        <>
          <label className="maisight-sources-field">
            <span>GitHub repository URL</span>
            <input
              type="url"
              value={gitUrl}
              required
              placeholder="https://github.com/acme/api-specs"
              aria-invalid={gitUrl.length > 0 && !gitValid}
              onChange={(e) =>
                onChange({ ...draft, config: { git_url: e.target.value, branch, path } })
              }
            />
            {gitUrl.length > 0 && !gitValid ? (
              <span className="maisight-sources-field-error" role="alert">
                Enter a github.com repo URL (GitLab/Bitbucket arrives in v1.2).
              </span>
            ) : null}
          </label>
          <label className="maisight-sources-field">
            <span>Branch</span>
            <input
              type="text"
              value={branch}
              onChange={(e) =>
                onChange({ ...draft, config: { ...draft.config, branch: e.target.value } })
              }
            />
          </label>
          <label className="maisight-sources-field">
            <span>Path to redocly.yaml</span>
            <input
              type="text"
              value={path}
              onChange={(e) =>
                onChange({ ...draft, config: { ...draft.config, path: e.target.value } })
              }
            />
          </label>
        </>
      )}
      <label className="maisight-sources-field">
        <span>Bearer token (optional)</span>
        <input
          type="password"
          value={draft.auth_value}
          placeholder="for private repos / specs"
          onChange={(e) => onChange({ ...draft, auth_value: e.target.value })}
        />
      </label>
    </div>
  );
}
